import { useDispatch } from 'react-redux'
import { useEffect, useCallback, useState } from 'react'
import { OrderSummaryV2 } from '../../../modules/MyPlan/components/OrderSummaryV2/OrderSummaryV2'

import Loader from '../../../common/Loader'
import { mapMeals } from '../../../modules/MyPlan/components/OrderSummaryV2/utils'
import { changeDateAndTimeslot } from '../../../redux-api-bridge/actions'
import { getUserMenuByDate } from '../../../redux-api-bridge/menu/effects'
import { setActiveOrder } from '../../../redux-api-bridge/order/actions'
import { selectDelivery } from '../../../redux/slices/selectedDelivery/slice'

import { toaster } from '../../../utils/toaster'

import { ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { useReconfirmOrder } from '../hooks/useReconfirmOrder'
import useCheckoutData from '../hooks/useCheckoutData'

import './AbuseOrderConfirmation.scss'
import { ResponsiveModalOrBottomSheet } from '../../../components/ResponsiveModalOrBottomSheet/ResponsiveModalOrBottomSheet'
import { CuiButton } from 'front-lib'
import tracking from '../../../shared/tracking'
import { fetchCartForDate } from '../../../redux-api-bridge/cart/effects'

type Props = {
  abuseCaseId: number
  date: string
  onBack: (withError?: boolean) => void
}

const AbuseOrderConfirmation = ({ date, onBack, abuseCaseId }: Props) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, AnyAction>>()
  const [isInitialized, setIsInitialized] = useState(false)
  const { handleOrderUpdate, status, error, onError } = useReconfirmOrder(
    abuseCaseId,
    date
  )

  const {
    productsWithStock,
    selectedUpcomingDay,
    deliveryIndex,
    timeSlot,
    plan,
    deliveryAddress
  } = useCheckoutData(date)

  useEffect(() => {
    if (isInitialized && status === 'initial') {
      if (!selectedUpcomingDay?.order?.id) {
        toaster.error('Error fetching your order details.')
        onBack(true)
      }
    }
  }, [isInitialized, selectedUpcomingDay?.order?.id, onBack, status])

  useEffect(() => {
    if (selectedUpcomingDay !== undefined) {
      const initializeData = async () => {
        try {
          await Promise.all([
            dispatch(setActiveOrder({ date })),
            dispatch(selectDelivery(deliveryIndex)),
            dispatch(getUserMenuByDate(date, false, false)),
            dispatch(fetchCartForDate(date)),
            dispatch(
              changeDateAndTimeslot({
                newDate: selectedUpcomingDay,
                cutOff: selectedUpcomingDay.cutoff,
                scheduled: selectedUpcomingDay.scheduled,
                newTimeslot: timeSlot
              })
            )
          ])

          // requestAnimationFrame is a browser API that schedules a function to be called before the next repaint.
          // In this context, we're using it to ensure that React's state updates are properly batched and synchronized with the browser's rendering cycle.
          requestAnimationFrame(() => {
            setIsInitialized(true)
          })
        } catch (error) {
          toaster.error('Error fetching your order details.')
          onBack(true)
        }
      }

      initializeData()
    }
  }, [dispatch, selectedUpcomingDay, date, deliveryIndex, timeSlot, onBack])

  const onConfirm = useCallback(() => {
    tracking.track('Discount Abuse Confirm Order Clicked', {
      page_name: 'Discount Abuse'
    })
    const products = productsWithStock.map(meal => ({
      id: meal.magento_id,
      qty: meal.qty,
      batch_id: meal.batch_id,
      inventoryId: meal.inventoryId
    }))

    const freeProducts = (selectedUpcomingDay?.order?.freeProducts ?? []).map(
      product => ({
        id: parseInt(product.product.id),
        qty: product.qty,
        inventoryId: product.product.inventoryId
      })
    )

    handleOrderUpdate({
      products,
      freeProducts,
      timeSlot: {
        start: timeSlot.start,
        end: timeSlot.end
      },
      orderId: selectedUpcomingDay.order.id,
      tip: selectedUpcomingDay.order.tip
    })
  }, [
    productsWithStock,
    selectedUpcomingDay?.order?.freeProducts,
    selectedUpcomingDay?.order?.id,
    selectedUpcomingDay?.order?.tip,
    timeSlot,
    handleOrderUpdate
  ])

  if (!isInitialized || status === 'loading' || !productsWithStock.length) {
    return <Loader />
  }

  return (
    <div className="abuse-order-confirmation">
      <OrderSummaryV2
        // @ts-expect-error SummaryV2 is not typed
        onBack={() => {
          tracking.track('Discount Abuse Back Button Clicked', {
            page_name: 'Discount Abuse'
          })
          onBack()
        }}
        handleFieldChange={() => 0}
        onConfirm={onConfirm}
        selectedDate={date}
        plan={plan}
        timeSlots={[timeSlot]}
        selectedTimeSlot={timeSlot}
        address={deliveryAddress}
        products={productsWithStock.map(mapMeals)}
      />
      <ErrorModal isOpen={!!error} onClose={onError} error={error} />
    </div>
  )
}

export default AbuseOrderConfirmation

const ErrorModal = ({
  isOpen,
  onClose,
  error
}: {
  isOpen: boolean
  onClose: () => void
  error: {
    type: 'case' | 'order'
  } | null
}) => {
  const title =
    error?.type === 'order'
      ? 'Something went wrong while processing your order'
      : 'An error occurred while processing your case'

  const message =
    error?.type === 'case'
      ? 'Oops! Something went wrong. Please try again later or contact support.'
      : 'This may be due to item availability or another issue. Please review your selections and try again'

  const cta = error?.type === 'case' ? 'OK' : 'Review cart'

  return (
    <ResponsiveModalOrBottomSheet
      onClose={() => 0}
      isOpen={isOpen}
      className="abuse-user-page__error-modal">
      <header>
        <h1>{title}</h1>
      </header>
      <main>
        <p>{message}</p>
      </main>
      <CuiButton size="large" onClick={onClose}>
        {cta}
      </CuiButton>
    </ResponsiveModalOrBottomSheet>
  )
}
